import React from 'react';
import { useTranslate, linkToRecord } from 'ra-core';
import { Link } from 'react-router-dom';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import CardActions from '@material-ui/core/CardActions';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import AppsIcon from '@material-ui/icons/Apps';
import TimeIcon from '@material-ui/icons/AccessTime';
import EventIcon from '@material-ui/icons/Event';
import format from 'date-fns/format';

import { MarkdownView, Stats, StatsText } from '../ui';
import { TagsField } from './TagsField';
import { ApiStatus } from './ApiStatus';

export const ApiCard = ({ basePath, record }) => {
    const classes = useStyles();
    const translate = useTranslate();
    const formattedDate =
        record && record.modifyTs ? format(record.modifyTs, 'P') : '';
    return (
        <Card
            className={classes.root}
            component={Link}
            to={linkToRecord(basePath, record && record.id, 'show')}
        >
            <CardHeader
                className={classes.header}
                title={
                    <Tooltip title={record.name}>
                        <Typography
                            variant="h6"
                            component="span"
                            display="block"
                            className={classes.title}
                            noWrap
                        >
                            {record.name}
                        </Typography>
                    </Tooltip>
                }
                disableTypography
                subheader={
                    <>
                        <div className={classes.subheader}>
                            <ApiStatus record={record} variant="caption" />
                            <Divider
                                orientation="vertical"
                                className={classes.divider}
                            />
                            <Typography
                                variant="caption"
                                color="textPrimary"
                                className={classes.ssgServiceType}
                            >
                                {record.ssgServiceType}
                            </Typography>
                            <Divider
                                orientation="vertical"
                                className={classes.divider}
                            />
                            <Tooltip title={record.version}>
                                <Typography
                                    variant="caption"
                                    color="textPrimary"
                                    noWrap
                                >
                                    {translate(
                                        'resources.apis.list.cards.fields.version',
                                        {
                                            version: record.version,
                                        }
                                    )}
                                </Typography>
                            </Tooltip>
                        </div>
                    </>
                }
            />
            <CardContent className={classes.content}>
                <Tooltip title={record.description || ''}>
                    {/* <MarkdownView
                        className={classes.description}
                        value={record.description}
                    /> */}
                    <div className={classes.description}>
                        {record.description}
                    </div>
                </Tooltip>
            </CardContent>
            <CardActions className={classes.footer}>
                <Grid container alignItems="center" className={classes.tags}>
                    <TagsField
                        record={record}
                        source="tags"
                        className={classes.tag}
                    />
                </Grid>
                <Grid container alignItems="center" className={classes.stats}>
                    <Grid item>
                        <Stats
                            icon={<AppsIcon />}
                            title={translate(
                                'resources.apis.list.cards.fields.applications_long',
                                {
                                    smart_count: record.applicationUsage || 0,
                                }
                            )}
                        >
                            <StatsText>
                                {translate(
                                    'resources.apis.list.cards.fields.applications',
                                    {
                                        smart_count:
                                            record.applicationUsage || 0,
                                    }
                                )}
                            </StatsText>
                        </Stats>
                    </Grid>
                    <Grid item>
                        <Stats
                            icon={<TimeIcon />}
                            title={translate(
                                'resources.apis.list.cards.fields.averageLatency_long',
                                {
                                    smart_count: record.averageLatency || 0,
                                }
                            )}
                        >
                            <StatsText>
                                {translate(
                                    'resources.apis.list.cards.fields.averageLatency',
                                    {
                                        ms: record.averageLatency || 0,
                                    }
                                )}
                            </StatsText>
                        </Stats>
                    </Grid>
                    {formattedDate && (
                        <Grid item>
                            <Stats
                                icon={<EventIcon />}
                                title={translate(
                                    'resources.apis.list.cards.fields.updated',
                                    {
                                        date: formattedDate,
                                    }
                                )}
                            >
                                <StatsText>{formattedDate}</StatsText>
                            </Stats>
                        </Grid>
                    )}
                </Grid>
            </CardActions>
        </Card>
    );
};

const useStyles = makeStyles(
    theme => ({
        root: {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            cursor: 'pointer',
            textDecoration: 'none',
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
        },
        header: {
            borderBottomColor: theme.palette.divider,
            borderBottomWidth: 1,
            borderBottomStyle: 'solid',
        },
        title: {
            wordBreak: 'break-word',
            maxWidth: '100%',
        },
        subheader: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            fontFamily: theme.typography.body2.fontFamily,
            fontSize: theme.typography.caption.fontSize,
            padding: theme.spacing(1, 0),
        },
        footer: {
            display: 'flex',
            flexDirection: 'column',
            flex: 0,
        },
        description: {
            flex: 1,
            overflow: 'hidden',
            lineClamp: 3,
            boxOrient: 'vertical',
            display: '-webkit-box',
            fontSize: '16px',
            fontFamily: 'adelle-sans-regular, sans-serif',
            fontWeight: 400,
            lineHeight: '20px',
            letterSpacing: '0.08px',
        },
        ssgServiceType: {
            fontWeight: theme.typography.fontWeightBold,
        },
        divider: {
            marginLeft: theme.spacing(),
            marginRight: theme.spacing(),
            minHeight: theme.spacing(2),
        },
        contentDivider: {
            width: '100%',
            marginBottom: theme.spacing(),
            marginTop: 0,
        },
        stats: {
            marginTop: 'auto',
        },
        tags: {
            borderTopStyle: 'solid',
            borderTopWidth: '1px',
            borderTopColor: theme.palette.grey[400],
            minHeight: theme.spacing(4),
            marginBottom: theme.spacing(1),
        },
        tag: {
            borderRadius: theme.spacing(0.5),
            fontWeight: theme.typography.fontWeightBold,
            '& + &': {
                marginLeft: theme.spacing(0.5),
            },
        },
    }),
    {
        name: 'Layer7ApiCard',
    }
);
